import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, SESSION_TOKEN_KEY } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, KeyRound, ArrowRight, LogOut } from "lucide-react";

const OWNER_CONTACT = "derrickpatenaude@uwalumni.com";

export default function BetaAccess() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data?.allowed) {
          // Already approved — bounce to dashboard
          navigate("/dashboard", { replace: true });
          return;
        }
        setMe(res.data);
        // Auto-redeem any invite code captured at /login?invite=XXXX
        const pending = localStorage.getItem("niko_pending_invite");
        if (pending) {
          localStorage.removeItem("niko_pending_invite");
          try {
            const r = await axios.post(
              `${API}/beta/redeem`, { code: pending },
              { withCredentials: true },
            );
            if (r.data?.allowed) {
              toast.success("Invite redeemed. Welcome.");
              navigate("/dashboard", { replace: true });
              return;
            }
          } catch (_) {
            setCode(pending); // surface the code so they can edit + retry
            toast.error("That invite code isn't valid — check it below");
          }
        }
      } catch (_) {
        navigate("/login", { replace: true });
      }
    };
    load();
  }, [navigate]);

  const redeem = async () => {
    const c = code.trim().toUpperCase();
    if (!c) {
      toast.error("Enter your invite code");
      return;
    }
    setRedeeming(true);
    try {
      const res = await axios.post(
        `${API}/beta/redeem`,
        { code: c },
        { withCredentials: true },
      );
      if (res.data?.allowed) {
        toast.success("You're in. Welcome to the beta.");
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn't work");
    } finally {
      setRedeeming(false);
    }
  };

  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    localStorage.removeItem(SESSION_TOKEN_KEY);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950
                    flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-2xl
                        shadow-2xl p-8 space-y-6">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500
                            flex items-center justify-center text-white font-bold shadow-lg">
              N
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-violet-400">
                Niko OS
              </div>
              <div className="text-sm text-zinc-400">Closed beta</div>
            </div>
          </div>

          {/* Headline */}
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full
                            bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
              <Lock className="w-3.5 h-3.5" />
              Invitation only
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              You're authenticated, but not yet on the beta list.
            </h1>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Niko OS is in closed beta with a small group of healthcare leaders
              shaping the product. You can either redeem an invite code from the
              owner, or request access and we'll get back to you.
            </p>
          </div>

          {/* Account chip */}
          {me && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/40 border border-zinc-800">
              <Mail className="w-4 h-4 text-zinc-400" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Signed in as</div>
                <div className="text-sm text-white truncate">{me.email}</div>
              </div>
              <button
                onClick={signOut}
                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                data-testid="beta-signout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          )}

          {/* Redeem code */}
          <div className="space-y-3">
            <Label className="text-zinc-300 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-violet-400" />
              Have an invite code?
            </Label>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. PHYX8EAR"
                maxLength={16}
                className="bg-zinc-900 border-zinc-700 text-white tracking-widest font-mono uppercase"
                data-testid="beta-code-input"
                onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
              />
              <Button
                onClick={redeem}
                disabled={redeeming || !code.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white font-semibold whitespace-nowrap"
                data-testid="beta-redeem-btn"
              >
                {redeeming ? "Checking…" : (
                  <>
                    Redeem <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Codes are case-insensitive. They expire after 30 days unless the owner extends them.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-600 uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Request access */}
          <div className="space-y-2">
            <p className="text-sm text-zinc-300">
              Don't have a code? Email the owner directly with your role and which
              hospital you're at.
            </p>
            <a
              href={`mailto:${OWNER_CONTACT}?subject=Niko OS beta access request&body=Hi%2C%0A%0AI%27d like access to the Niko OS beta.%0A%0AMy role%3A%0AHospital%3A%0AWhy I%27d find this useful%3A%0A%0AThanks%2C`}
              className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 text-sm font-semibold"
              data-testid="beta-request-access-link"
            >
              <Mail className="w-4 h-4" />
              {OWNER_CONTACT}
            </a>
          </div>
        </div>
        <p className="text-center text-xs text-zinc-600 mt-6">
          Niko OS · Director's Operating System · Closed beta
        </p>
      </div>
    </div>
  );
}
